import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSEO } from "@/hooks/use-seo";

export default function EarlyAccessApplication() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useSEO({
    title: "Apply for Early Access | The Lifecycle Canvas",
    description: "Request early access to The Lifecycle Canvas for Interim Executives.",
    noIndex: true,
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setError("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const application = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("/api/early-access/applications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(application),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          typeof result.error === "string"
            ? result.error
            : "Your application could not be submitted.",
        );
      }
      setSubmitted(true);
      form.reset();
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Your application could not be submitted. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Layout>
      <section className="relative overflow-hidden px-6 py-16 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
        <div className="relative mx-auto max-w-2xl">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-500/20 bg-teal-500/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-teal-300">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Early access
          </div>
          {submitted ? (
            <div className="rounded-2xl border border-emerald-500/20 bg-slate-900/90 p-8 shadow-2xl sm:p-10">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" aria-hidden="true" />
              <h1 className="mt-5 text-3xl font-display font-bold text-white">
                Your application is with us
              </h1>
              <p className="mt-3 leading-7 text-slate-300">
                Thanks for your interest. We’ll review your request and contact you by email with an update.
              </p>
              <Link
                href="/"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-teal-300 transition-colors hover:text-teal-200"
              >
                Return to the home page <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/90 p-8 shadow-2xl sm:p-10">
              <h1 className="text-3xl font-display font-bold text-white sm:text-4xl">
                Apply for early access
              </h1>
              <p className="mt-3 leading-7 text-slate-400">
                We’re opening The Lifecycle Canvas to a small group of Interim Executives. Tell us a little about yourself and how you’d use it.
              </p>

              <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="applicant-name" className="mb-2 block text-sm font-medium text-slate-200">
                    Your name <span className="text-teal-300">*</span>
                  </label>
                  <Input
                    id="applicant-name"
                    name="fullName"
                    autoComplete="name"
                    required
                    minLength={2}
                    maxLength={160}
                    placeholder="Alex Morgan"
                    className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                  />
                </div>

                <div>
                  <label htmlFor="applicant-email" className="mb-2 block text-sm font-medium text-slate-200">
                    Work email <span className="text-teal-300">*</span>
                  </label>
                  <Input
                    id="applicant-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    placeholder="alex@example.com"
                    className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="applicant-organization" className="mb-2 block text-sm font-medium text-slate-200">
                      Organisation <span className="text-slate-500">(optional)</span>
                    </label>
                    <Input
                      id="applicant-organization"
                      name="organization"
                      autoComplete="organization"
                      maxLength={160}
                      placeholder="Company or consultancy"
                      className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="applicant-role" className="mb-2 block text-sm font-medium text-slate-200">
                      Current role <span className="text-slate-500">(optional)</span>
                    </label>
                    <Input
                      id="applicant-role"
                      name="role"
                      maxLength={120}
                      placeholder="Interim CEO"
                      className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="applicant-use" className="mb-2 block text-sm font-medium text-slate-200">
                    What would you use the canvas for? <span className="text-slate-500">(optional)</span>
                  </label>
                  <Textarea
                    id="applicant-use"
                    name="intendedUse"
                    maxLength={2000}
                    rows={4}
                    placeholder="A few sentences about your work or what you hope to plan."
                    className="resize-y border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                  />
                </div>

                <div
                  className="absolute -left-[10000px] h-px w-px overflow-hidden"
                  aria-hidden="true"
                >
                  <label htmlFor="applicant-website">Leave this field blank</label>
                  <input
                    id="applicant-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                {error && (
                  <p role="alert" className="rounded-lg border border-rose-500/30 bg-rose-950/40 px-4 py-3 text-sm text-rose-200">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="w-full bg-teal-700 py-6 text-base font-semibold text-white hover:bg-teal-600"
                  data-testid="submit-early-access"
                >
                  {isSubmitting ? "Sending application…" : "Request early access"}
                  {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />}
                </Button>

                <p className="text-center text-xs leading-5 text-slate-500">
                  Your details will be used to review your request and contact you about access.
                </p>
              </form>
            </div>
          )}
          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an approved account?{" "}
            <Link href="/sign-in" className="font-medium text-teal-300 hover:text-teal-200">
              Sign in
            </Link>
          </p>
        </div>
      </section>
    </Layout>
  );
}
